import { useState } from 'react';
import {
  checkBrowserCompatibility,
  getRecommendedBrowsers,
} from '../lib/browserCompatibility';

export function CompatibilityWarning({ onClose }: { onClose?: () => void }) {
  const [showDetails, setShowDetails] = useState(false);

  const checks = checkBrowserCompatibility();
  const recommendedBrowsers = getRecommendedBrowsers();

  const criticalIssues = checks.filter(
    (check) => check.critical && !check.supported
  );

  // Don't show warning if no critical issues (when used as standalone modal)
  if (criticalIssues.length === 0 && !onClose) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="text-3xl">⚠️</div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Browser Not Supported
                </h2>
                <p className="text-gray-600">
                  Your browser is missing essential features required for Turbo
                  Trainer
                </p>
              </div>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            )}
          </div>

          {/* Critical Issues */}
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <h3 className="font-semibold text-red-800 mb-2 flex items-center">
              <span className="mr-2">🚫</span>
              Missing Required Features
            </h3>
            <ul className="space-y-2">
              {criticalIssues.map((check, index) => (
                <li key={index} className="text-sm text-red-700">
                  <strong>{check.feature}:</strong> {check.description}
                </li>
              ))}
            </ul>
            <p className="text-sm text-red-800 mt-3 font-medium">
              The app cannot function without these features.
            </p>
          </div>

          {/* Recommended Browsers */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
              <span className="mr-2">✅</span>
              Use One of These Browsers Instead
            </h3>
            <div className="space-y-2">
              {recommendedBrowsers.map((browser, index) => (
                <div
                  key={index}
                  className="flex items-start space-x-3 p-3 bg-green-50 border border-green-200 rounded"
                >
                  <div className="text-green-600 mt-0.5">🌐</div>
                  <div>
                    <div className="font-medium text-green-800">
                      {browser.name}
                    </div>
                    <div className="text-sm text-green-700">
                      {browser.reason}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Feature Details Toggle */}
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="mb-4 text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            {showDetails ? '↑ Hide' : '↓ Show'} technical details
          </button>

          {/* Detailed Report */}
          {showDetails && (
            <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <h3 className="font-semibold text-gray-800 mb-3">
                Browser Compatibility Report
              </h3>
              <div className="space-y-2">
                {checks.map((check, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 bg-white rounded border"
                  >
                    <div className="flex items-center space-x-2">
                      <span className={check.supported ? '✅' : '❌'}></span>
                      <span className="font-medium">{check.feature}</span>
                      {check.critical && (
                        <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded">
                          Required
                        </span>
                      )}
                    </div>
                    <span
                      className={`text-sm ${check.supported ? 'text-green-600' : 'text-red-600'}`}
                    >
                      {check.supported ? 'Supported' : 'Not Supported'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Button */}
          <div className="flex justify-center">
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
            >
              Refresh Page
            </button>
          </div>

          <p className="mt-4 text-sm text-gray-600 text-center">
            💡 For the best experience, use Google Chrome or Microsoft Edge on
            desktop
          </p>
        </div>
      </div>
    </div>
  );
}
